import React from 'react';
import { Activity, AlertTriangle, Calendar, Clock, DollarSign, ExternalLink, Eye, FileText, Flame, GitBranch, ListTodo, MailCheck, MousePointerClick, PhoneCall, Send, Smartphone, Target, TrendingUp, UserCircle, Users, Zap } from 'lucide-react';
import { Button } from '../../ui/button';
import { HotBadge, StatCard, StatusBadge } from '../shared';
import { PIPELINE_ORDER, STATUS_CONFIG, isHotLead, timeAgo } from '../../../utils/admin-helpers';
import { useAdminCtx } from '../AdminDashboardContext';

export default function OverviewTab() {
  const { dashboard, followUpMetrics, hotCount, leads, priorityQueue, revenueOnly, setActiveTab, setReadFilter, setRevenueOnly, setScoreFilter, setSelectedLeadId, setSortBy, setStatusFilter, staleCount } = useAdminCtx();
  const d = dashboard || {};
  return (
    <>
      {/* ── Who to Contact Next ── */}
      {priorityQueue && priorityQueue.queue.length > 0 && (
        <div className="mb-6 bg-slate-900 rounded-xl border border-slate-800 overflow-hidden" data-testid="priority-queue-card">
          <div className="px-5 pt-5 pb-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-violet-500/20 flex items-center justify-center">
                <Target className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-white font-bold text-base">Who to Contact Next</h2>
                <p className="text-slate-500 text-xs">{priorityQueue.total_active} active leads &middot; ranked by priority</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* High Priority Alert */}
              {priorityQueue.high_priority_not_contacted_today > 0 && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/25 text-red-400 text-xs font-bold animate-pulse" data-testid="high-pri-not-contacted">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {priorityQueue.high_priority_not_contacted_today} high-priority not contacted today
                </span>
              )}
              {/* Revenue Toggle */}
              <button
                onClick={() => setRevenueOnly(v => !v)}
                data-testid="revenue-only-toggle"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${
                  revenueOnly
                    ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                    : 'bg-slate-950 border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'
                }`}
              >
                <DollarSign className="h-3.5 w-3.5" />
                {revenueOnly ? 'Revenue Only' : 'Show Revenue Only'}
              </button>
            </div>
          </div>

          {/* Queue List */}
          <div className="divide-y divide-slate-800" data-testid="priority-queue-list">
            {priorityQueue.queue.map((lead, idx) => {
              const ps = lead.priority;
              const es = lead.engagement_score || ps?.engagement_score;
              const company = (lead.company || '').split(' | ')[0] || '';
              const scoreCfg = es?.label === 'hot'
                ? { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30' }
                : es?.label === 'warm'
                ? { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30' }
                : { bg: 'bg-slate-500/15', text: 'text-slate-400', border: 'border-slate-500/30' };
              const priColor = ps.priority_score >= 60 ? 'text-red-400' : ps.priority_score >= 40 ? 'text-amber-400' : 'text-slate-400';
              const priBar = ps.priority_score >= 60 ? 'from-red-500 to-orange-500' : ps.priority_score >= 40 ? 'from-amber-500 to-yellow-500' : 'from-slate-600 to-slate-500';

              return (
                <div
                  key={lead.id}
                  className="px-5 py-3 flex items-center gap-4 hover:bg-slate-800/40 transition-colors group"
                  data-testid={`priority-lead-${idx}`}
                >
                  {/* Rank */}
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    idx === 0 ? 'bg-cyan-500/20 text-cyan-400 ring-1 ring-cyan-500/30' :
                    idx < 3 ? 'bg-slate-800 text-slate-300' : 'bg-slate-900 text-slate-500'
                  }`}>
                    {idx + 1}
                  </div>

                  {/* Lead Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-medium text-sm truncate">{lead.name}</span>
                      <StatusBadge status={lead.status || 'new'} />
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md ${scoreCfg.bg} ${scoreCfg.text} ${scoreCfg.border} border text-[10px] font-bold`}>
                        {es?.score ?? 0}
                      </span>
                      {lead.proposal_value > 0 && (
                        <span className="text-emerald-400 text-xs font-medium">${lead.proposal_value.toLocaleString()}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {company && <span className="text-slate-500 text-xs truncate max-w-[160px]">{company}</span>}
                      {ps.last_activity && (
                        <>
                          <span className="text-slate-600 text-xs">&middot;</span>
                          <span className="text-slate-500 text-xs">Active {timeAgo(ps.last_activity)}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Priority Score */}
                  <div className="flex-shrink-0 w-20 hidden sm:block">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-slate-500 text-[10px]">Priority</span>
                      <span className={`text-xs font-bold ${priColor}`}>{ps.priority_score}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className={`h-full rounded-full bg-gradient-to-r ${priBar}`} style={{ width: `${ps.priority_score}%` }} />
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                    {lead.phone && (
                      <a
                        href={`tel:${lead.phone}`}
                        onClick={e => e.stopPropagation()}
                        className="p-2 rounded-lg hover:bg-emerald-500/15 text-slate-400 hover:text-emerald-400 transition-colors"
                        title="Call"
                        data-testid={`pq-call-${idx}`}
                      >
                        <PhoneCall className="h-4 w-4" />
                      </a>
                    )}
                    {lead.phone && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedLeadId(lead.id); }}
                        className="p-2 rounded-lg hover:bg-blue-500/15 text-slate-400 hover:text-blue-400 transition-colors"
                        title="Send SMS"
                        data-testid={`pq-sms-${idx}`}
                      >
                        <Smartphone className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedLeadId(lead.id)}
                      className="p-2 rounded-lg hover:bg-cyan-500/15 text-slate-400 hover:text-cyan-400 transition-colors"
                      title="Open Lead"
                      data-testid={`pq-open-${idx}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Hot Prospect Alert */}
      {hotCount > 0 && (
        <div className="mb-6 p-4 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center gap-3" data-testid="hot-prospect-alert">
          <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
            <Flame className="h-5 w-5 text-orange-400 animate-pulse" />
          </div>
          <div>
            <p className="text-orange-300 font-semibold text-sm">
              {hotCount} hot prospect{hotCount > 1 ? 's' : ''} in the last 24 hours
            </p>
            <p className="text-orange-400/70 text-xs mt-0.5">
              Respond quickly to maximize conversion
            </p>
          </div>
          <Button
            onClick={() => { setActiveTab('leads'); setReadFilter('unread'); }}
            variant="outline"
            size="sm"
            className="ml-auto border-orange-500/30 text-orange-300 hover:bg-orange-500/10"
            data-testid="view-hot-leads-button"
          >
            View Leads
          </Button>
        </div>
      )}

      {/* Stale Leads Alert (48h no activity) */}
      {staleCount > 0 && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-3" data-testid="stale-leads-alert">
          <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-400 animate-pulse" />
          </div>
          <div>
            <p className="text-red-300 font-semibold text-sm">
              {staleCount} lead{staleCount > 1 ? 's' : ''} with no activity for 48+ hours
            </p>
            <p className="text-red-400/70 text-xs mt-0.5">
              These leads haven't been contacted yet — respond now to avoid losing them
            </p>
          </div>
          <Button
            onClick={() => { setActiveTab('leads'); setStatusFilter('new'); }}
            variant="outline"
            size="sm"
            className="ml-auto border-red-500/30 text-red-300 hover:bg-red-500/10"
            data-testid="view-stale-leads-button"
          >
            View Leads
          </Button>
        </div>
      )}

      {/* Activity Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mb-4">
        <StatCard icon={Users} label="Total Leads" value={d.total_leads || 0} color="cyan" subtext={`${d.unread_leads || 0} unread`} />
        <StatCard icon={MousePointerClick} label="CTA Clicks" value={d.cta_clicks || 0} color="violet" />
        <StatCard icon={FileText} label="Form Submissions" value={d.form_submissions || 0} color="emerald" />
        <StatCard icon={Calendar} label="Calendly Views" value={d.calendly_views || 0} color="amber" />
        <StatCard icon={TrendingUp} label="Conversion Rate" value={`${d.conversion_rate || 0}%`} color="blue" subtext="CTA to Form" />
        <StatCard icon={Eye} label="Section Views" value={d.section_views || 0} color="rose" />
      </div>

      {/* Revenue + Follow-up KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
        <StatCard icon={DollarSign} label="Pipeline Value" value={`$${((d.revenue?.total_pipeline || 0) / 1000).toFixed(0)}k`} color="emerald" subtext={d.revenue?.weighted_forecast ? `$${(d.revenue.weighted_forecast / 1000).toFixed(0)}k weighted` : undefined} />
        <StatCard icon={DollarSign} label="Won Revenue" value={`$${((d.revenue?.won_revenue || 0) / 1000).toFixed(0)}k`} color="cyan" subtext={d.revenue?.avg_deal_size ? `$${d.revenue.avg_deal_size.toLocaleString()} avg deal` : undefined} />
        <StatCard icon={UserCircle} label="Unassigned" value={d.follow_up?.unassigned || 0} color="amber" subtext="leads need owner" />
        <StatCard icon={ListTodo} label="Tasks Due" value={(d.follow_up?.tasks_today || 0) + (d.follow_up?.tasks_overdue || 0)} color="rose" subtext={d.follow_up?.tasks_overdue ? `${d.follow_up.tasks_overdue} overdue` : 'on track'} />
      </div>

      {/* CTA Breakdown + Pipeline + Recent Leads */}
      <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
        {/* CTA Clicks by Section */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="cta-breakdown">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <MousePointerClick className="h-4 w-4 text-violet-400" /> CTA Clicks by Section
          </h3>
          {Object.keys(d.cta_by_section || {}).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(d.cta_by_section || {}).map(([section, count]) => {
                const maxCount = Math.max(...Object.values(d.cta_by_section || {}));
                const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
                return (
                  <div key={section}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-300 capitalize">{section.replace(/_/g, ' ')}</span>
                      <span className="text-white font-medium">{count}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No CTA click data yet</p>
          )}
        </div>

        {/* Pipeline Summary */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="pipeline-summary">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <GitBranch className="h-4 w-4 text-emerald-400" /> Pipeline Overview
          </h3>
          {Object.keys(d.pipeline_summary || {}).length > 0 ? (
            <div className="space-y-2.5">
              {PIPELINE_ORDER.map(status => {
                const count = (d.pipeline_summary || {})[status] || 0;
                if (count === 0) return null;
                const total = d.total_leads || 1;
                const pct = Math.round((count / total) * 100);
                const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
                return (
                  <button
                    key={status}
                    className="w-full text-left group"
                    onClick={() => { setActiveTab('leads'); setStatusFilter(status); }}
                    data-testid={`pipeline-${status}`}
                  >
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-300 group-hover:text-white transition-colors">{cfg.label}</span>
                      <span className="text-white font-medium">{count} <span className="text-slate-500 text-xs">({pct}%)</span></span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${cfg.color.includes('emerald') ? 'bg-emerald-500' : cfg.color.includes('blue') ? 'bg-blue-500' : cfg.color.includes('violet') ? 'bg-violet-500' : cfg.color.includes('amber') ? 'bg-amber-500' : cfg.color.includes('red') ? 'bg-red-500' : 'bg-slate-500'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No pipeline data yet</p>
          )}
        </div>

        {/* Recent Leads */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="recent-leads">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Clock className="h-4 w-4 text-cyan-400" /> Recent Leads
          </h3>
          {(d.recent_leads || []).length > 0 ? (
            <div className="space-y-3">
              {(d.recent_leads || []).map((lead) => {
                const hot = isHotLead(lead.created_at);
                return (
                  <button
                    key={lead.id || lead.email}
                    onClick={() => setSelectedLeadId(lead.id)}
                    className={`w-full text-left flex items-start gap-3 p-3 rounded-lg bg-slate-950 border ${hot ? 'border-orange-500/30' : 'border-slate-800'} hover:border-slate-600 transition-colors`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${hot ? 'bg-orange-500/15' : 'bg-cyan-500/10'}`}>
                      {hot ? <Flame className="h-4 w-4 text-orange-400" /> : <span className="text-cyan-400 text-xs font-bold">{lead.name?.[0] || '?'}</span>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-medium truncate">{lead.name}</p>
                        {hot && <HotBadge />}
                        <StatusBadge status={lead.status || 'new'} />
                      </div>
                      <p className="text-slate-400 text-xs truncate">{lead.email}</p>
                      <p className="text-slate-500 text-xs mt-1">
                        {hot ? <span className="text-orange-400">{timeAgo(lead.created_at)}</span> : new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    {!lead.read && <span className="w-2 h-2 rounded-full bg-cyan-400 mt-2 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No leads yet</p>
          )}
        </div>
      </div>

      {/* Follow-Up Nurture Metrics */}
      {followUpMetrics && (
        <div className="mt-6 bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="follow-up-metrics">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <MailCheck className="h-4 w-4 text-cyan-400" /> Follow-Up Nurture Sequence
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
              <p className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">Not Booked</p>
              <p className="text-2xl font-bold text-amber-400" data-testid="fu-not-booked">{followUpMetrics.not_booked}</p>
              <p className="text-slate-500 text-xs mt-0.5">submitted but no appointment</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
              <p className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">In Sequence</p>
              <p className="text-2xl font-bold text-violet-400" data-testid="fu-in-sequence">{followUpMetrics.in_sequence}</p>
              <p className="text-slate-500 text-xs mt-0.5">actively being nurtured</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
              <p className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">Booked via Follow-Up</p>
              <p className="text-2xl font-bold text-cyan-400" data-testid="fu-booked">{followUpMetrics.booked_after_follow_up}</p>
              <p className="text-slate-500 text-xs mt-0.5">converted after nurture emails</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
              <p className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">Conversion Rate</p>
              <p className="text-2xl font-bold text-emerald-400" data-testid="fu-conversion">{followUpMetrics.follow_up_conversion_rate}%</p>
              <p className="text-slate-500 text-xs mt-0.5">follow-up to booking</p>
            </div>
          </div>
          <div className="flex gap-3 mt-3 text-xs text-slate-500">
            <span>Paused: {followUpMetrics.paused}</span>
            <span>Completed: {followUpMetrics.completed_sequence}</span>
            <span>Stopped: {followUpMetrics.stopped}</span>
            <span>Total Booked: {followUpMetrics.booked_total}</span>
          </div>
        </div>
      )}

      {/* Speed to Lead */}
      {dashboard?.speed_to_lead && (
        <div className="mt-6 bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="speed-to-lead">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-400" /> Speed to Lead
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
              <p className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">Avg Response</p>
              <p className="text-xl font-bold text-amber-400" data-testid="stl-avg">
                {dashboard.speed_to_lead.avg_minutes ? `${dashboard.speed_to_lead.avg_minutes < 60 ? `${dashboard.speed_to_lead.avg_minutes}m` : `${(dashboard.speed_to_lead.avg_minutes / 60).toFixed(1)}h`}` : '—'}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
              <p className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">Fastest</p>
              <p className="text-xl font-bold text-emerald-400" data-testid="stl-fastest">
                {dashboard.speed_to_lead.fastest_minutes ? `${dashboard.speed_to_lead.fastest_minutes < 60 ? `${dashboard.speed_to_lead.fastest_minutes}m` : `${(dashboard.speed_to_lead.fastest_minutes / 60).toFixed(1)}h`}` : '—'}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
              <p className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">Contacted</p>
              <p className="text-xl font-bold text-cyan-400" data-testid="stl-contacted">{dashboard.speed_to_lead.total_contacted}</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
              <p className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">Awaiting Contact</p>
              <p className={`text-xl font-bold ${dashboard.speed_to_lead.awaiting_contact > 0 ? 'text-red-400' : 'text-slate-400'}`} data-testid="stl-awaiting">{dashboard.speed_to_lead.awaiting_contact}</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
              <p className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">Auto-Responses</p>
              <p className="text-xl font-bold text-violet-400" data-testid="stl-auto">{dashboard.speed_to_lead.auto_responses_sent}</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
              <p className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">SMS Sent</p>
              <p className="text-xl font-bold text-blue-400" data-testid="stl-sms">{dashboard.speed_to_lead.sms_sent}</p>
            </div>
          </div>
        </div>
      )}

      {/* Engagement Score Distribution */}
      {dashboard?.engagement && (
        <div className="mt-6 bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="engagement-distribution">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-cyan-400" /> Lead Engagement Scores
          </h3>

          {/* Hot Leads Not Contacted Alert */}
          {dashboard.engagement.hot_not_contacted > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-3" data-testid="hot-not-contacted-alert">
              <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <Flame className="h-4 w-4 text-red-400 animate-pulse" />
              </div>
              <div className="flex-1">
                <p className="text-red-300 font-semibold text-sm">
                  {dashboard.engagement.hot_not_contacted} hot lead{dashboard.engagement.hot_not_contacted > 1 ? 's' : ''} not contacted in 24h
                </p>
                <p className="text-red-400/70 text-xs mt-0.5">These are your highest-value leads — reach out now</p>
              </div>
              <Button
                onClick={() => { setActiveTab('leads'); setSortBy('score_desc'); setScoreFilter('hot'); }}
                variant="outline"
                size="sm"
                className="border-red-500/30 text-red-300 hover:bg-red-500/10 flex-shrink-0"
                data-testid="view-hot-not-contacted"
              >
                Contact Now
              </Button>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => { setActiveTab('leads'); setScoreFilter('hot'); setSortBy('score_desc'); }}
              className="p-4 rounded-lg bg-slate-950 border border-slate-800 hover:border-red-500/30 transition-colors group text-left"
              data-testid="engagement-hot"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider group-hover:text-red-400 transition-colors">Hot</span>
              </div>
              <p className="text-3xl font-bold text-red-400">{dashboard.engagement.hot}</p>
              <p className="text-slate-500 text-xs mt-1">Score 70-100</p>
            </button>
            <button
              onClick={() => { setActiveTab('leads'); setScoreFilter('warm'); setSortBy('score_desc'); }}
              className="p-4 rounded-lg bg-slate-950 border border-slate-800 hover:border-amber-500/30 transition-colors group text-left"
              data-testid="engagement-warm"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider group-hover:text-amber-400 transition-colors">Warm</span>
              </div>
              <p className="text-3xl font-bold text-amber-400">{dashboard.engagement.warm}</p>
              <p className="text-slate-500 text-xs mt-1">Score 40-69</p>
            </button>
            <button
              onClick={() => { setActiveTab('leads'); setScoreFilter('cold'); setSortBy('score_desc'); }}
              className="p-4 rounded-lg bg-slate-950 border border-slate-800 hover:border-slate-600 transition-colors group text-left"
              data-testid="engagement-cold"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-slate-500" />
                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider group-hover:text-slate-300 transition-colors">Cold</span>
              </div>
              <p className="text-3xl font-bold text-slate-400">{dashboard.engagement.cold}</p>
              <p className="text-slate-500 text-xs mt-1">Score 0-39</p>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
